'use client';

import React, { useMemo } from 'react';
import ProductDetailsAdapter from 'components/commercetools-ui/organisms/product/product-details/helpers/adapter';
import Reviews from 'components/commercetools-ui/organisms/reviews';
import { transformToNestedStructure } from 'helpers/utils/transformToNestedStructure';
import { useCart } from 'frontastic/hooks';
import useProductReviews from './hooks/useProductReviews';
import { ProductDetailsTasticProps } from './types';

const ProductDetailsTastic: React.FC<ProductDetailsTasticProps> = ({ data, origin }) => {
  const { addItem, data: cart } = useCart();
  // Transform flat data structure to nested objects
  const nestedData = useMemo(() => transformToNestedStructure(data), [data]);

  const product = data.product?.dataSource?.product;
  if (!product || !product.productId) {
    return null;
  }

  const content = data.content?.dataSource?.content;
  const assets = data.assets?.dataSource?.assets;

  // Set up helper data from configuration
  const woodFinishHelper = data.finishHelperText
    ? {
        text: data.finishHelperText,
        linkLabel: data.finishHelperLinkLabel,
        link: data.finishHelperLink,
      }
    : undefined;

  const wirebrushModal =
    data.wirebrushTitle && data.wirebrushText
      ? {
          title: data.wirebrushTitle,
          text: data.wirebrushText,
          images: data.wirebrushImages,
        }
      : undefined;

  // Get nested data sections for reviews configuration
  const reviewsConfig = {
    ratingsGeneral: nestedData.ratingsGeneral || {},
    ratingsSorting: nestedData.ratingsSorting || {},
    ratingsLoggedIn: nestedData.ratingsLoggedIn || {},
    ratingsLoggedOut: nestedData.ratingsLoggedOut || {},
    ratingsForm: nestedData.ratingsForm || {},
  };

  // Use our custom hook to handle all review functionality
  const {
    hasReviews,
    isReviewDrawerOpen,
    ratingStatsProps,
    reviewListProps,
    reviewFormProps,
    bannerTitle,
    bannerBody,
    bannerButtonLabel,
    emptyBannerTitle,
    emptyBannerBody,
    handleBannerClick,
    handleDrawerClose,
  } = useProductReviews({
    productId: product.productId,
    reviewRatingStatistics: product.reviewRatingStatistics,
    configData: reviewsConfig,
  });

  return (
    <>
      <ProductDetailsAdapter
        assets={assets}
        cart={cart}
        content={content}
        onAddToCart={addItem}
        origin={origin}
        product={product}
        reviewListProps={reviewListProps}
        wirebrushModal={wirebrushModal}
        woodFinishHelper={woodFinishHelper}
      />
      <Reviews
        title={reviewsConfig.ratingsGeneral.productReviewsTitle}
        ratingStatsProps={ratingStatsProps}
        reviewListProps={reviewListProps}
        hasReviews={hasReviews}
        rateBannerTitle={bannerTitle}
        rateBannerBody={bannerBody}
        emptyBannerTitle={emptyBannerTitle}
        emptyBannerBody={emptyBannerBody}
        reviewFormProps={reviewFormProps}
        onRateClick={handleBannerClick}
        isDrawerOpen={isReviewDrawerOpen}
        onClose={handleDrawerClose}
        bannerButtonLabel={bannerButtonLabel}
      />
    </>
  );
};

export default ProductDetailsTastic;
